import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { type MutableRefObject, useMemo, useState } from 'react';

import { DrawerBody } from '@growtherapy/sprout-ui';

import {
  MICMeasureBundleResponses,
  MeasureBundlesListConnected,
  findMostRecentCompletedPHQBundle,
  useShouldPromptForCSSRS,
} from '@grow-therapy-team/seedling-components';

import { logger } from '../../datadog/logger';
import { toast } from '../../toasts';
import { ClientInformationDrawerHeader } from '../client-information/ClientInformationDrawerHeader';
import { useSelectedClientAppointmentIds } from '../client-information/useSelectedClientAppointmentIds';
import {
  ProviderDrawerState,
  drawerStateAtom,
  isInSessionWithSelectedClientAtom,
  providerShortIdAtom,
  selectedBundleAtom,
  selectedClientInfoAtom,
  selectedClientMeasuresData as selectedClientMeasuresDataAtom,
  selectedMeasureBundleItemIdAtom,
} from '../state';
import { useGetMeasuresByPatientShortId } from '../useGetMeasuresByPatientShortId';
import { CSSRSPromptAlert } from './CSSRSPromptAlert';
import {
  type MeasureResponsesTrackingContext,
  type MeasureResponsesTrackingState,
  useMeasureResponsesDrawerTrackingContext,
  useTrackMeasureResponsesDrawerView,
} from './useTrackMeasureResponsesDrawerView';
import { PROMIS_TRIGGERS, buildCSSRSAlertTabPredicate, getCSSRSPromptToastCopy } from './utils';

type MeasureResponsesProps = MeasureResponsesTrackingContext;

export function MeasureResponses({ entryMethod, resetEntryMethod, trackingState }: MeasureResponsesProps) {
  const measureBundle = useAtomValue(selectedBundleAtom);
  const measureBundleItemId = useAtomValue(selectedMeasureBundleItemIdAtom);
  const selectedClientInfo = useAtomValue(selectedClientInfoAtom);
  const patientShortId = selectedClientInfo?.patientShortId;
  const providerShortId = useAtomValue(providerShortIdAtom);
  const inSession = useAtomValue(isInSessionWithSelectedClientAtom);
  const [cssrsAlertIsDismissed, setCssrsAlertIsDismissed] = useState(false);
  const { appointmentShortId } = useSelectedClientAppointmentIds();

  useGetMeasuresByPatientShortId(patientShortId);
  const measureBundles = useAtomValue(selectedClientMeasuresDataAtom);
  const preferredFirstName = selectedClientInfo?.preferredName?.patientFirstName;
  const setDrawerState = useSetAtom(drawerStateAtom);

  const { loading: cssrsLoading, result: cssrsResult } = useShouldPromptForCSSRS({
    patientShortId: patientShortId ?? '',
    measureBundles: measureBundles ?? [],
  });

  const cssrsCopy = useMemo(
    () => getCSSRSPromptToastCopy(cssrsResult, preferredFirstName ?? '', true, true, measureBundles),
    [cssrsResult, preferredFirstName, measureBundles],
  );

  const mostRecentPHQ9bundle = findMostRecentCompletedPHQBundle(measureBundles ?? []);
  const isMostRecentPHQ9Bundle = mostRecentPHQ9bundle?.measureBundleShortId === measureBundle?.measureBundleShortId;
  const isPromisTrigger = PROMIS_TRIGGERS.includes(cssrsResult.triggerSource ?? '');

  const shouldShowCssrsAlert =
    !cssrsAlertIsDismissed && !cssrsLoading && Boolean(cssrsCopy) && (isPromisTrigger || isMostRecentPHQ9Bundle);

  const cssrsAlertTabPredicate = isPromisTrigger ? buildCSSRSAlertTabPredicate(cssrsResult.triggerSource) : undefined;

  useTrackMeasureResponsesDrawerView({
    appointmentShortId,
    cssrsLoading,
    entryMethod,
    hasCssrsAlert: shouldShowCssrsAlert,
    inSession,
    measureBundle,
    measureBundleItemId,
    patientShortId,
    providerShortId,
    resetEntryMethod,
    trackingState,
  });

  const onAdministerCSSRS = () => {
    setDrawerState(ProviderDrawerState.CSSRS_FORM);
  };

  const onNavigateToPaperworkFlow = () => {
    setDrawerState(ProviderDrawerState.PENDING_MEASURES);
  };

  if (!measureBundle) return null;

  return (
    <MICMeasureBundleResponses
      key={measureBundle.measureBundleShortId}
      cssrsAlert={
        shouldShowCssrsAlert && cssrsCopy ? (
          <CSSRSPromptAlert
            className='mb-8'
            onClose={() => setCssrsAlertIsDismissed(true)}
            onAdministerCSSRS={onAdministerCSSRS}
            heading={cssrsCopy.heading}
            body={cssrsCopy.body}
            callToAction={cssrsCopy.callToAction}
          />
        ) : undefined
      }
      cssrsAlertTabPredicate={cssrsAlertTabPredicate}
      initialSelectedMeasureBundleItemId={measureBundleItemId}
      measureBundle={measureBundle}
      onNavigateToPaperworkFlow={onNavigateToPaperworkFlow}
      isInverse
    />
  );
}

function MeasureBundlesList() {
  const setSelectedBundle = useSetAtom(selectedBundleAtom);
  const patientShortId = useAtomValue(selectedClientInfoAtom)?.patientShortId;

  if (!patientShortId) return null;

  return (
    <MeasureBundlesListConnected
      patientChartQueryOptions={{
        onError: (error) => {
          logger.error('Unable to fetch measure bundles list', { patientShortId }, error);
          toast.error('Unable to fetch measures. Please try again later.');
        },
      }}
      patientShortId={patientShortId}
      onClickBundle={(measureBundle) => {
        setSelectedBundle(measureBundle);
      }}
      isInverse
    />
  );
}

function DrawerHeader() {
  const preferredFirstName = useAtomValue(selectedClientInfoAtom)?.preferredName?.patientFirstName;
  const [selectedBundle, setSelectedBundle] = useAtom(selectedBundleAtom);
  const setSelectedMeasureBundleItem = useSetAtom(selectedMeasureBundleItemIdAtom);
  const setDrawerState = useSetAtom(drawerStateAtom);
  const sentAt = selectedBundle?.sentAt;

  const drawerTitle = useMemo(() => {
    if (sentAt) {
      return `Check-in from ${new Date(sentAt).toLocaleDateString(undefined, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })}`;
    }
    if (preferredFirstName) {
      return `${preferredFirstName}'s Measures`;
    }
    return 'Measures';
  }, [sentAt, preferredFirstName]);

  const goToPrevious = () => {
    if (selectedBundle) {
      setSelectedBundle(undefined);
      setSelectedMeasureBundleItem(undefined);
      return;
    }

    setDrawerState((prev) => {
      if (prev === ProviderDrawerState.MEASURE_RESPONSES) {
        return ProviderDrawerState.CLIENT_INFORMATION;
      }

      return ProviderDrawerState.MEASURE_RESPONSES;
    });
  };

  return <ClientInformationDrawerHeader drawerTitle={drawerTitle} onClickBackButton={goToPrevious} />;
}

export function MeasureResponsesDrawer({
  trackingState,
}: {
  trackingState: MutableRefObject<MeasureResponsesTrackingState>;
}) {
  const hasSelectedBundle = Boolean(useAtomValue(selectedBundleAtom));
  const trackingContext = useMeasureResponsesDrawerTrackingContext(hasSelectedBundle, trackingState);

  return (
    <>
      <DrawerHeader />
      <DrawerBody>
        <div className='fs-exclude' data-dd-privacy='mask'>
          {hasSelectedBundle ? <MeasureResponses {...trackingContext} /> : <MeasureBundlesList />}
        </div>
      </DrawerBody>
    </>
  );
}
